/**
 * Product Filters — the collapsible box above the Retail season panel.
 *
 * Click the header to open a checkbox matrix of every product (MERAC sales
 * element) sold this season, Retail and Tea Room side by side. Ticking
 * elements filters EVERY figure on the page — the season cards, both daily
 * tables and the exports — to those elements. Default is "All items" (nothing
 * ticked = the unfiltered caches, exactly the page as it was).
 *
 * Two special filters sit above the matrix and are mutually exclusive:
 *   Thank You Voucher Sales — only sales paid with a Gift Aid Thank You voucher.
 *   Member Sales            — only sales to members (card scanned or a
 *                             membership discount applied).
 *
 * Selection keys are "Category:Analysis" pairs, so "Branded Souvenirs" in the
 * shops and the same element in a tea room can be filtered independently.
 * The page owns the state; this component only renders and reports changes.
 */
import { useMemo } from "react";
import type { AnalysisRow, Segment } from "./salesInsightsTypes";

export function pairKey(category: string, analysis: string): string {
  return `${category}:${analysis}`;
}

export interface ProductFiltersProps {
  open: boolean;
  onToggle: () => void;
  analyses: AnalysisRow[] | null;
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  segment: Segment | null;
  onSegmentChange: (s: Segment | null) => void;
  disabled: boolean;
  disabledReason?: string;
  /** When set, the two special segment filters are greyed out with this reason (the
   *  product matrix stays usable) — Kathryn's Variant 1 has no Member / Thank You split. */
  segmentsDisabledReason?: string;
  fmt: (n: number) => string;
}

const CATS = ["Retail", "Tea Room"] as const;

export function describeFilters(selected: Set<string>, segment: Segment | null): string {
  const parts: string[] = [];
  if (selected.size > 0) parts.push(`${selected.size} product${selected.size === 1 ? "" : "s"}`);
  if (segment === "thankyou") parts.push("Thank You voucher sales");
  if (segment === "member") parts.push("Member sales");
  return parts.length ? parts.join(" · ") : "All items";
}

export function ProductFilters(props: ProductFiltersProps) {
  const { open, onToggle, analyses, selected, onSelectedChange, segment, onSegmentChange, disabled, disabledReason, segmentsDisabledReason, fmt } = props;
  const active = selected.size > 0 || segment != null;
  const summary = disabled ? "Till-takings basis only" : describeFilters(selected, segment);

  const byCat = useMemo(() => {
    const out: Record<string, AnalysisRow[]> = { Retail: [], "Tea Room": [] };
    for (const r of analyses ?? []) (out[r.category] ??= []).push(r);
    return out;
  }, [analyses]);

  const toggle = (k: string) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onSelectedChange(next);
  };
  const clearCat = (cat: string) => {
    const next = new Set([...selected].filter((k) => !k.startsWith(`${cat}:`)));
    onSelectedChange(next);
  };
  const clearAll = () => {
    onSelectedChange(new Set());
    onSegmentChange(null);
  };

  return (
    <section className={"sales-filters" + (open ? " open" : "") + (active ? " active" : "")} aria-label="Product filters">
      <button
        type="button"
        className="sales-filters-head"
        onClick={onToggle}
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? disabledReason : open ? "Hide the product filters" : "Choose which products the page shows"}
      >
        <span className="sales-filters-title">Product Filters</span>
        <span className={"sales-filters-summary" + (active ? " on" : "")}>{summary}</span>
        <span className="sales-filters-chev" aria-hidden="true">{open ? "▲" : "▼"}</span>
      </button>

      {open && !disabled && (
        <div className="sales-filters-body">
          <div className="sales-filters-special" role="group" aria-label="Special filters" title={segmentsDisabledReason}>
            <label className={"sales-filters-check sales-filters-check--special" + (segment === "thankyou" ? " on" : "") + (segmentsDisabledReason ? " disabled" : "")}>
              <input
                type="checkbox"
                checked={segment === "thankyou"}
                disabled={Boolean(segmentsDisabledReason)}
                onChange={(e) => onSegmentChange(e.target.checked ? "thankyou" : null)}
              />
              <span>
                <strong>Thank You Voucher Sales</strong>
                <small>Only sales paid with a Gift Aid Thank You voucher</small>
              </span>
            </label>
            <label className={"sales-filters-check sales-filters-check--special" + (segment === "member" ? " on" : "") + (segmentsDisabledReason ? " disabled" : "")}>
              <input
                type="checkbox"
                checked={segment === "member"}
                disabled={Boolean(segmentsDisabledReason)}
                onChange={(e) => onSegmentChange(e.target.checked ? "member" : null)}
              />
              <span>
                <strong>Member Sales</strong>
                <small>Only sales to members — card scanned or a membership discount applied</small>
              </span>
            </label>
            <button type="button" className="sales-clear sales-filters-clear" onClick={clearAll} disabled={!active}>
              Clear filters
            </button>
          </div>

          <div className="sales-filters-cols">
            {CATS.map((cat) => {
              const items = byCat[cat] ?? [];
              const ticked = items.filter((i) => selected.has(pairKey(cat, i.analysis))).length;
              return (
                <div className="sales-filters-col" key={cat}>
                  <label className={"sales-filters-check sales-filters-all" + (ticked === 0 ? " on" : "")}>
                    <input type="checkbox" checked={ticked === 0} onChange={() => clearCat(cat)} disabled={ticked === 0} />
                    <span>
                      <strong>All items</strong>
                      <small>{cat} · {items.length} product{items.length === 1 ? "" : "s"} this season{ticked ? ` · ${ticked} ticked` : ""}</small>
                    </span>
                  </label>
                  {analyses == null ? (
                    <div className="sales-filters-empty">Loading products…</div>
                  ) : items.length === 0 ? (
                    <div className="sales-filters-empty">No {cat.toLowerCase()} products sold this season.</div>
                  ) : (
                    <div className="sales-filters-grid">
                      {items.map((i) => {
                        const k = pairKey(cat, i.analysis);
                        const on = selected.has(k);
                        return (
                          <label key={k} className={"sales-filters-check" + (on ? " on" : "")}>
                            <input type="checkbox" checked={on} onChange={() => toggle(k)} />
                            <span>
                              {i.analysis}
                              <small>{fmt(i.net)}</small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="sales-filters-note">
            Filters apply to every figure on this page — the season cards, both daily tables and the Excel / PDF exports.
            Budget comparisons are hidden while a filter is active, because budgets are for all items.
            A product filter and a special filter combine: ticking Toys &amp; Games with Member Sales shows members' toy purchases.
          </p>
        </div>
      )}
    </section>
  );
}
